import type { CompanyDiaryResourceKindKey, CompanyDiaryResourceSummary } from '@/api/companyDiary';

/**
 * The entry modal's tab set, shared with the surfaces that open it on a chosen tab.
 *
 * The list index deep-links into these - its Addresses, Staff, Transport,
 * Containers and Trailers cells each open the modal on their own tab - so the tab
 * order and keys cannot live inside the modal any more: two copies would drift and
 * a stale index would open the wrong panel.
 */

/** The four resource pickers, in tab order. */
export const RESOURCE_KINDS = ['staff', 'transport', 'container', 'trailer'] as const;

export type TabKey = 'general' | 'addresses' | CompanyDiaryResourceKindKey | 'notes' | 'files';

export const TAB_LABELS: Record<TabKey, string> = {
    general: 'General',
    addresses: 'Addresses',
    staff: 'Staff',
    transport: 'Transport',
    container: 'Containers',
    trailer: 'Trailers',
    notes: 'Notes',
    files: 'Files',
};

/**
 * Tab order, and the only place it is written down. The modal's panels switch on
 * the key; `MuvalTabs` works in indices, so this array is what converts between
 * them.
 */
export const TAB_KEYS = [
    'general',
    'addresses',
    ...RESOURCE_KINDS,
    'notes',
    'files',
] as const satisfies readonly TabKey[];

/** An unknown key opens General rather than an empty modal body. */
export const tabIndexOf = (key: TabKey | null | undefined): number => {
    const index = key ? TAB_KEYS.indexOf(key) : -1;

    return index === -1 ? 0 : index;
};

/**
 * An entry's saved vessels, split by the `type` the API sends back on each one.
 *
 * A vessel with no type falls to containers rather than being dropped: losing an
 * assignment on load would silently unassign it on the next save, which is worse
 * than filing it in the wrong tab where the operator can see and move it.
 */
export const splitVessels = (vessels: CompanyDiaryResourceSummary[]) => ({
    containers: vessels.filter((vessel) => vessel.type !== 'TRAILER'),
    trailers: vessels.filter((vessel) => vessel.type === 'TRAILER'),
});
